import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { getTierByCi, TIERS, getCiProgress } from '../lib/press'
import RankBadge from '../components/RankBadge'

export default function TheRecordPage() {
  const { id } = useParams()
  const [writer, setWriter] = useState(null)
  const [ciLog, setCiLog] = useState([])
  const [posts, setPosts] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function load() {
      const [profileRes, logRes, postsRes] = await Promise.all([
        supabase.from('profiles').select('*').eq('id', id).single(),
        supabase.from('ci_log').select('*').eq('user_id', id)
          .order('created_at', { ascending: false }).limit(50),
        supabase.from('posts').select('id, title, created_at, status')
          .eq('author_id', id).eq('status', 'published')
          .order('created_at', { ascending: false }),
      ])
      setWriter(profileRes.data)
      setCiLog(logRes.data || [])
      setPosts(postsRes.data || [])
      setLoading(false)
    }
    load()
  }, [id])

  if (loading) return <div style={s.page}><p style={s.muted}>Loading...</p></div>
  if (!writer) return <div style={s.page}><p style={s.muted}>Writer not found.</p></div>

  const ci = writer.column_inches || 0
  const tier = getTierByCi(ci, writer.is_voice)
  const nextTier = writer.is_voice ? null : TIERS[tier.rank] || null
  const progress = getCiProgress(ci, tier.rank)

  // Build CI timeline from log
  const totalEarned = ciLog.reduce((sum, e) => sum + (e.ci > 0 ? e.ci : 0), 0)

  const ACTION_LABELS = {
    post_culture_short: 'Published short piece',
    post_culture_mid: 'Published piece',
    post_culture_long: 'Published long piece',
    post_journal: 'Published journal piece',
    reaction_brought_here: '◈ Brought Me Here reaction',
    reaction_shifts_something: '↻ Shifts Something reaction',
    reaction_sits_with_me: '◐ Sits With Me reaction',
    reaction_other: 'Reaction received',
    comment_received: 'Comment on a piece',
    admin_award: 'Editorial award',
    admin_deduct: 'Editorial deduction',
    post_deleted_revert: 'Post deleted (reverted)',
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <Link to={`/writer/${id}`} style={s.back}>← {writer.display_name}'s Profile</Link>
        <p style={s.eyebrow}>The Record</p>
        <h1 style={s.title}>{writer.display_name}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
          <RankBadge columnInches={ci} isVoice={writer.is_voice || false} size="md" />
          <span style={s.ciTotal}>{ci} CI total</span>
        </div>
      </div>

      <div className="record-layout">
        {/* Left: rank progress */}
        <aside className="record-sidebar">
          <p style={s.sideLabel}>Rank Progress</p>

          {/* Current rank */}
          <div style={{ ...s.rankCard, borderColor: tier.color }}>
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', color: tier.color, letterSpacing: '0.12em', marginBottom: '0.25rem' }}>{tier.indicator}</div>
            <div style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.25rem', fontWeight: 700, color: tier.color }}>{tier.title}</div>
            <div style={s.muted}>{tier.description}</div>
          </div>

          {/* Progress to next */}
          {nextTier && (
            <div style={s.progressBlock}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.4rem' }}>
                <span style={s.progressLabel}>{progress}% to {nextTier.title}</span>
                <span style={{ ...s.progressLabel, color: 'var(--ink)' }}>{nextTier.ci - ci} CI needed</span>
              </div>
              <div style={s.progressBar}>
                <div style={{ ...s.progressFill, width: `${progress}%`, background: tier.color }} />
              </div>
            </div>
          )}

          {/* All ranks */}
          <p style={{ ...s.sideLabel, marginTop: '1.5rem' }}>The Press</p>
          {TIERS.slice(0, 11).map(t => (
            <div key={t.rank} style={{ ...s.tierRow, opacity: tier.rank >= t.rank ? 1 : 0.35 }}>
              <span style={{ color: tier.rank >= t.rank ? t.color : 'var(--muted)', fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', minWidth: 80 }}>{t.title}</span>
              <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', color: 'var(--muted)' }}>{t.ci} CI</span>
              {tier.rank === t.rank && <span style={{ color: t.color, fontSize: '0.6rem' }}>◆</span>}
            </div>
          ))}

          <div style={s.statBlock}>
            <div style={s.stat}><span style={s.statNum}>{posts.length}</span><span style={s.statLabel}>pieces published</span></div>
            <div style={s.stat}><span style={s.statNum}>{totalEarned}</span><span style={s.statLabel}>CI earned</span></div>
          </div>
        </aside>

        {/* Right: CI log */}
        <main className="record-main">
          <p style={s.sideLabel}>CI History</p>
          {ciLog.length === 0 ? (
            <div className="empty-state"><p>No CI history yet.</p></div>
          ) : (
            <div style={s.logList}>
              {ciLog.map(e => (
                <div key={e.id} style={s.logRow}>
                  <div style={{ flex: 1 }}>
                    <div style={s.logAction}>{ACTION_LABELS[e.action] || e.action}</div>
                    <div style={s.logDate}>{new Date(e.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                  </div>
                  <div style={{ ...s.logCI, color: e.ci > 0 ? '#4db87a' : 'var(--accent)' }}>
                    {e.ci > 0 ? '+' : ''}{e.ci} CI
                    {e.multiplier && e.multiplier > 1 && (
                      <span style={{ fontSize: '0.5rem', color: 'var(--muted)', marginLeft: 4 }}>×{e.multiplier}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

const s = {
  page: { paddingTop: '4.5rem', minHeight: '100vh' },
  header: { padding: '3rem 3rem 2rem', borderBottom: '1px solid var(--rule)' },
  back: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.08em', color: 'var(--muted)', textTransform: 'uppercase', display: 'block', marginBottom: '1rem' },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.4rem' },
  title: { fontFamily: "'Space Grotesk', sans-serif", fontSize: 'clamp(2rem, 5vw, 3rem)', fontWeight: 700, letterSpacing: '-0.02em' },
  ciTotal: { fontFamily: "'DM Mono', monospace", fontSize: '0.7rem', color: 'var(--muted)', letterSpacing: '0.06em' },
  layout: { display: 'grid', gridTemplateColumns: '260px 1fr', alignItems: 'start' },
  sidebar: { padding: '2rem 1.5rem', borderRight: '1px solid var(--rule)', position: 'sticky', top: '4.5rem', maxHeight: 'calc(100vh - 4.5rem)', overflowY: 'auto' },
  sideLabel: { fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.75rem' },
  rankCard: { padding: '1rem 1.25rem', border: '1.5px solid', borderRadius: 8, marginBottom: '1rem', background: 'var(--cream)' },
  progressBlock: { marginBottom: '1rem' },
  progressLabel: { fontFamily: "'DM Mono', monospace", fontSize: '0.56rem', color: 'var(--muted)', letterSpacing: '0.06em' },
  progressBar: { height: 4, background: 'var(--rule)', borderRadius: 2, overflow: 'hidden' },
  progressFill: { height: '100%', transition: 'width 0.6s ease', borderRadius: 2 },
  tierRow: { display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.3rem 0', borderBottom: '1px solid var(--rule)' },
  statBlock: { marginTop: '1.5rem', display: 'flex', gap: '1rem' },
  stat: { display: 'flex', flexDirection: 'column', gap: '0.15rem' },
  statNum: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.5rem', fontWeight: 700, color: 'var(--accent)', lineHeight: 1 },
  statLabel: { fontFamily: "'DM Mono', monospace", fontSize: '0.52rem', color: 'var(--muted)', letterSpacing: '0.08em', textTransform: 'uppercase' },
  main: { padding: '2rem 3rem' },
  logList: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  logRow: { display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.85rem 1.1rem', background: 'var(--cream)', border: '1px solid var(--rule)', borderRadius: 8 },
  logAction: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.88rem', color: 'var(--ink)', marginBottom: '0.2rem' },
  logDate: { fontFamily: "'DM Mono', monospace", fontSize: '0.56rem', color: 'var(--muted)', letterSpacing: '0.06em' },
  logCI: { fontFamily: "'DM Mono', monospace", fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.06em', flexShrink: 0 },
  muted: { fontFamily: "'DM Mono', monospace", fontSize: '0.7rem', color: 'var(--muted)', marginTop: '0.3rem' },
}
